<template>
  <ActionPanelShell title="Send a text" :subtitle="subtitle" @close="closeSlideOutPanel">
    <div class="sp" data-test="send-text-panel">
      <div v-if="initialLoading" class="sp-skel" aria-hidden="true" data-test="send-panel-loading"><i></i><i></i><i></i><i></i></div>

      <template v-else>
        <section class="sp-sec" data-test="send-text-compose">
          <PanelNotice v-if="apErrorText" tone="error" dismissible data-test="send-panel-error" @dismiss="apDismissError">{{ apErrorText }}</PanelNotice>
          <PanelNotice v-if="smsUnavailableReason" tone="warn" data-test="send-text-unavailable">{{ smsUnavailableReason }}</PanelNotice>

          <div class="sp-from" data-test="send-text-from">
            <i class="fa fa-phone" aria-hidden="true"></i>
            <span>From {{ siteName }}<template v-if="formattedSmsNumber"> · {{ formattedSmsNumber }}</template></span>
          </div>

          <div class="sp-head">
            <span class="sp-h">Message</span>
            <span v-if="selectedWiki.length" class="sp-aside" data-test="send-text-from-template">
              From <b>{{ selectedWiki[0].Name }}</b>
              <button type="button" class="sp-link" data-test="send-text-clear-template" @click="clearTemplate">Clear</button>
            </span>
          </div>

          <MessageComposer :variables="emailInsertVariables" :emoji="emojiTable" @insert="onInsert">
            <template #field>
              <textarea
                ref="smsTextarea"
                v-model="smsMessage"
                rows="6"
                placeholder="Write your message"
                aria-label="Message"
                data-test="send-text-message"
                @input="onSmsTextareaInput"
              ></textarea>
            </template>
            <template #status>
              <!-- Shared SMS guidance: the live credit count + the warn / block banner. onSmsGuidance
                   (PatientSidebar) turns a block into smsBlocked, which switches Send off. -->
              <SmsGuidance :text="smsMessage || ''" substitute-tags @change="onSmsGuidance" />
            </template>
          </MessageComposer>

          <!-- Send sits directly UNDER the message at every size (design 3a) so it shows without scrolling. -->
          <div class="sp-actions">
            <button
              type="button"
              class="sp-btn-primary"
              :disabled="sendDisabled"
              data-test="send-text-send"
              @click="sendText"
            ><i class="fa fa-paper-plane" aria-hidden="true"></i> {{ apBusy ? 'Sending…' : 'Send text' }}</button>
            <button type="button" class="sp-btn-ghost" data-test="send-text-cancel" @click="closeSlideOutPanel">Cancel</button>
          </div>
          <p v-if="smsBlocked" class="sp-note sp-note--bad" data-test="send-text-too-long">Too long to send as one text.</p>

          <PanelNotice v-if="hasFormLinks" tone="info" icon="fa-link" data-test="send-text-links-note">
            This message has a form link. Each [Form: …] turns into a link only {{ firstName }} can open. Edit the words around it freely; deleting the brackets drops the link.
          </PanelNotice>
          <PanelNotice v-if="templateTextModified && selectedWiki.length > 0" tone="warn" data-test="send-text-edited-note">
            You changed the template, so it sends as written, in English. Pick it again without editing to send it in {{ firstName }}’s preferred language.
          </PanelNotice>
          <p v-if="smsHasVariables" class="sp-note" data-test="send-text-vars-note">{{ varsNote }}</p>
        </section>

        <section class="sp-sec" data-test="send-text-templates">
          <div>
            <div class="sp-h">Templates</div>
            <p class="sp-sub">Pick one to fill in the message. You can edit it before sending.</p>
          </div>
          <TemplatePicker
            :items="templateItems"
            :selected="selectedWiki.map(w => w.WikiPageID)"
            search-placeholder="Search text templates"
            empty-text="No text templates yet. Add one in the Wiki and tag it sms-template."
            :limit="5"
            @toggle="onTemplateToggle"
          >
            <template #above-list>
              <PanelNotice v-if="hiddenReviewTemplates > 0" tone="warn" icon="fa-star" data-test="send-text-review-note">
                Review request templates aren’t listed here — their review link is made for each patient. Send one with the Review button.
              </PanelNotice>
            </template>
          </TemplatePicker>
        </section>
      </template>
    </div>
  </ActionPanelShell>
</template>

<script>
/**
 * Send a text — the redesigned panel (design "Patient Action Panels" 2d, 3a), opened by
 * PatientPanelActionHost for the Patient Profile page and the Schedule View popover.
 *
 * `extends` PatientSidebar: template loading (sms-template pages), the greeting prefill, the
 * template-vs-edited rule, the send itself (sendText → /send-text-msg, the refusal and opt-out
 * handling) and the credit guidance all run the classic code. Only the template and the styles are
 * new: a quiet "From" line instead of the green box, the credit line under the message, Send directly
 * under the message, and a searchable template list instead of a paginated table.
 *
 * ⛔ Keeps `ref="smsTextarea"` — PatientSidebar.init() focuses it and insertAtCursor() splices into it.
 * ⛔ Closing is PatientSidebar.closeSlideOutPanel(): it emits `closePanel`, the one event the
 * slide-out library binds.
 */
import PatientSidebar from '@/pages/PatientProfile/components/PatientSidebar';
import SmsGuidance from '@/components/SmsGuidance.vue';
import ActionPanelShell from './ActionPanelShell.vue';
import MessageComposer from './MessageComposer.vue';
import TemplatePicker from './TemplatePicker.vue';
import PanelNotice from './PanelNotice.vue';
import sendPanelCommon, { formatPhoneLine } from './sendPanelCommon.js';
import { tagsOf } from '@/utils/wikiTags.js';

// The tag that put the page in this list (sms-template or email-template) and the form tag are on every row — not
// worth repeating there. (Matched by shape, not by name: the list itself is loaded and review-filtered by PatientSidebar.init().)
const isSystemTag = t => /-template$/.test(t) || t === 'form';

export default {
  name: 'SendTextPanel',

  extends: PatientSidebar,
  mixins: [sendPanelCommon],

  components: { ActionPanelShell, MessageComposer, TemplatePicker, PanelNotice, SmsGuidance },

  props: {
    // The host passes no sidebarType: this panel IS the Text one. (PatientSidebar requires it.)
    sidebarType: { type: String, default: 'Text', validator: v => v === 'Text' },
  },

  computed: {
    subtitle() {
      const phone = formatPhoneLine(this.patientData && this.patientData.WirelessPhone);
      return ['To ' + this.apPatientName, phone].filter(Boolean).join(' · ');
    },
    siteName() {
      return (this.user && this.user.SiteName) || 'Your practice';
    },
    firstName() {
      return (this.patientData && this.patientData.FName) || 'the patient';
    },
    hasFormLinks() {
      return !!(this.selectedTemplateHasFormLinks || Object.keys(this.templateLinkMap).length > 0 || /\[Form:/.test(this.smsMessage || ''));
    },
    varsNote() {
      return `Words in {{double braces}} fill in for ${this.firstName} when it sends.`;
    },
    templateItems() {
      return (this.wikiPages || []).map(w => ({
        id: w.WikiPageID,
        name: w.Name,
        desc: tagsOf(w).filter(t => !isSystemTag(t)).join(' · '),
      }));
    },
    sendDisabled() {
      return this.apBusy || !!this.smsUnavailableReason || this.smsBlocked || !(this.smsMessage || '').trim();
    },
  },

  methods: {
    onInsert(e) {
      if (e.kind === 'emoji') this.insertEmoji(e.emoji);
      else this.insertVariable(e.name);
    },

    // PatientSidebar.updateSelectedWiki toggles: picking selects (and prefills), picking the chosen one clears.
    onTemplateToggle(id) {
      const row = (this.wikiPages || []).find(w => w.WikiPageID === id);
      if (row) this.updateSelectedWiki(row);
    },

    clearTemplate() {
      if (this.selectedWiki.length) this.updateSelectedWiki(this.selectedWiki[0]);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './send-panel';

@include send-panel-base;
</style>
